<template>
  <v-container fluid>
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="refundData"
        :options.sync="options"
        item-key="id"
        :footer-props="footerProps"
        :loading-text="$t('refund.refundsLoading')"
        :no-data-text="$t('refund.noRefunds')"
        :mobile-breakpoint="mobileBreakpoint"
        :loading="loadingData"
        :server-items-length="totalRefunds"
        dense
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-text-field
              v-model="quickFilters.refund_id"
              :placeholder="$t('refund.searchRefund').toString()"
              outlined
              dense
              clearable
              hide-details
              class="mr-2"
              style="max-width: 220px"
              @input="applyQuickFilters"
            ></v-text-field>
            <v-text-field
              v-model="quickFilters.order_id"
              :placeholder="$t('refund.searchOrder').toString()"
              outlined
              dense
              clearable
              hide-details
              class="mr-2"
              style="max-width: 220px"
              @input="applyQuickFilters"
            ></v-text-field>
            <v-badge overlap :value="nbActiveFilters" :content="nbActiveFilters">
              <v-btn color="primary" outlined @click="displayFilters = !displayFilters">
                <v-icon>mdi-filter-plus-outline</v-icon>
              </v-btn>
            </v-badge>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined @click="reloadList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>

        <template v-slot:item.order_id="{ item }">
          <v-btn
            v-if="item.order_id && isCustomerService"
            class="px-0"
            text
            color="primary"
            @click="goToOrder(item.order_id)"
          >
            {{ item.order_id }}
          </v-btn>
          <span v-else-if="item.order_id">{{ item.order_id }}</span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.return_id="{ item }">
          <v-btn v-if="item.return_id" class="px-0" text color="primary" @click="goToReturn(item.return_id)">
            {{ item.return_id }}
          </v-btn>
          <span v-else>-</span>
        </template>

        <template v-slot:item.current_status="{ item }">
          <v-chip
            small
            :color="getStatusBackgroundColor(item.current_status)"
            :text-color="getStatusTextColor(item.current_status)"
          >
            {{ getStatusTranslation(item.current_status) }}
          </v-chip>
        </template>

        <template v-slot:item.amount="{ item }">
          <span>{{ item.amount | formatPrice(item.currency) }}</span>
          {{ item.currency ? item.currency.code : '' }}
        </template>

        <template v-slot:item.amount_exclude_tax="{ item }">
          <span v-if="item.amount_exclude_tax != null">
            {{ item.amount_exclude_tax | formatPrice(item.currency) }} {{ item.currency ? item.currency.code : '' }}
          </span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.source_location_id="{ item }">
          <span v-if="item.source_location_id">{{ item.source_location_id }}</span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.created_at="{ item }">
          <span v-if="item.created_at">{{ new Date(item.created_at) | moment('L LTS') }}</span>
          <span v-else>-</span>
        </template>

        <template v-slot:item.refund_date="{ item }">
          <span v-if="item.refund_date">{{ new Date(item.refund_date) | moment('L LTS') }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:item.customer="{ item }">
          <span>{{ getCustomerName(item) }}</span>
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn text color="primary" @click="goToDetail(item.refund_id)">
            {{ $t('refund.viewDetails') }}
          </v-btn>
        </template>
      </v-data-table>
      <filter-dialog
        :key="formFiltersKey"
        v-model="displayFilters"
        :title="$t('refund.filterRefunds')"
        :fields="filterFields"
        @confirm="filter"
        :loadingData="loadingData"
      ></filter-dialog>
    </v-card>
  </v-container>
</template>

<script>
  import { debounce } from 'lodash'
  import { mapState } from 'vuex'
  import { EventBus } from '@/event-bus'
  import { RefundService } from '../../services/refundService'
  import { textExists } from '../../helpers/helper'
  import {
    getRefundStatusBackgroundColor,
    getRefundStatusTextColor,
    getRefundStatuses,
    getRefundStatusTranslation,
  } from '../../helpers/refundHelper'

  export default {
    name: 'RefundTable',
    props: {
      /**
       * Store view: when true, the list is restricted to the connected store
       * (`currentStore` → `source_location_id`, enforced server-side) and the
       * store-code column is hidden.
       */
      restrictToCurrentStore: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        refundData: [],
        options: {
          page: 1,
          itemsPerPage: 15,
          sortBy: [],
          sortDesc: [],
        },
        loadingData: false,
        totalRefunds: 0,
        filters: [],
        displayFilters: false,
        formFiltersKey: Date.now(),
        mobileBreakpoint: 768,
        quickFilters: {
          refund_id: '',
          order_id: '',
        },
        locations: [],
        refundService: {},
        displayFieldSettings: null,
      }
    },
    created() {
      this.refundService = new RefundService(this.$auth)
      this.debouncedReload = debounce(this.reloadList, 300)
    },
    async mounted() {
      const tasks = [this.getSettingsByCountry()]
      // Central view only: load the location picker. The store view is scoped to a
      // single store, so the store-code filter is not needed.
      if (!this.restrictToCurrentStore) {
        tasks.push(this.getAllLocations())
      }
      await Promise.all(tasks)
    },
    activated() {
      const previousPath = this.$store.state.previousPath.split('/')[1]
      const currentBase = this.$route.path.split('/')[1]
      if (previousPath !== currentBase) {
        this.filters = []
        this.formFiltersKey = Date.now()
        this.options.itemsPerPage = 15
        this.options.page = 1
        this.options.sortBy = []
        this.options.sortDesc = []
      }
      this.refundData = []
      this.list()
    },
    methods: {
      async reloadList() {
        await this.$auth.isAuthenticated()
        this.options.page = 1
        this.list()
      },
      filter(filters) {
        this.filters = filters
        this.reloadList()
      },
      goToDetail(refundId) {
        // Keep the detail in the same context (store vs central) so its breadcrumb returns to the
        // list the user came from — and so it survives a refresh (the context is in the URL).
        const name = this.restrictToCurrentStore ? 'storeRefundDetails' : 'refundDetails'
        this.$router.push({ name, params: { id: refundId } })
      },
      // Order detail (/orders/:id) requires isCustomerService — the link is only rendered
      // for that role, so navigation always lands on the admin/customer-service detail page.
      goToOrder(orderId) {
        this.$router.push('/orders/' + orderId)
      },
      goToReturn(returnId) {
        this.$router.push('/returns/' + returnId)
      },
      applyQuickFilters() {
        this.debouncedReload()
      },
      async list() {
        this.loadingData = true
        const res = await this.refundService.list(this.options, this.filters, this.quickFilters, true, this.locationId)
        if (res) {
          this.refundData = res.data
          this.totalRefunds = res.nbRefunds
        }
        this.loadingData = false
      },
      getStatusTranslation(statusValue) {
        return getRefundStatusTranslation(statusValue)
      },
      getStatusBackgroundColor(status) {
        return getRefundStatusBackgroundColor(status)
      },
      getStatusTextColor(status) {
        return getRefundStatusTextColor(status)
      },
      /**
       * Loads the partner locations feeding the store-code filter (central view only).
       * Mirrors OrdersTableAdmin so the filter offers the same location picker as
       * `origin_location_id`. Bumps `formFiltersKey` so the dialog rebuilds once loaded.
       */
      async getAllLocations() {
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            itemsPerPage: false,
            fields: ['location_id', 'name'],
          },
        }
        try {
          const locations = await this.$Amplify.API.get('locations', '/v1/int/locations', myInit)
          this.locations = locations.data
          this.formFiltersKey = Date.now()
        } catch {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('orderWorkflow.locationsLoadingErrorMessage'),
            color: 'error',
          })
        }
      },
      /**
       * Loads the display settings driving customer-information visibility.
       * Tries the current location's country first; on failure, falls back to the default
       * settings — querying `FRA`. `displayFieldSettings` feeds the `addToHeaders`
       * mixin so the Customer column is hidden for roles not allowed to see customer data.
       * On total failure it stays null, so `addToHeaders` defaults to showing the column.
       */
      async getSettingsByCountry() {
        const countryId = this.currentStoreDetails?.address?.country_id ?? 'FRA'
        const settings = await this.fetchCountrySettings(countryId)
        this.displayFieldSettings = settings?.display?.customer_information ?? null
      },
      /**
       * Fetches the settings for a single country / default key. Returns null when
       * unavailable so callers can fall back without handling rejected promises.
       */
      async fetchCountrySettings(countryIso3) {
        const myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        try {
          return await this.$Amplify.API.get('settings', '/v1/settings/' + countryIso3, myInit)
        } catch (e) {
          if (e.response?.status === 404 && countryIso3 !== 'FRA') {
            console.log(`No settings for country ${this.currentStoreDetails?.address?.country_id}`)
            return await this.fetchCountrySettings('FRA')
          }
          return null
        }
      },
      getCustomerName(refund) {
        const customerValues = []
        if (textExists(refund?.customer?.first_name)) {
          customerValues.push(refund.customer.first_name)
        }
        if (textExists(refund?.customer?.last_name)) {
          customerValues.push(refund.customer.last_name)
        }
        if (customerValues.length > 0) {
          return customerValues.join(' ')
        }
        if (textExists(refund?.customer?.customer_id)) {
          return refund.customer.customer_id
        }
        return ''
      },
    },
    watch: {
      options: {
        handler() {
          this.list()
        },
      },
    },
    computed: {
      ...mapState(['isCustomerService', 'roles', 'currentStoreDetails', 'currentStore']),
      /**
       * Store scope forwarded to the service: the connected store in store view,
       * `null` (no restriction) in the central view.
       */
      locationId() {
        return this.restrictToCurrentStore ? this.currentStore : null
      },
      nbActiveFilters() {
        return this.filters.filter((f) => f.active).length
      },
      filterLocations() {
        return this.locations.map((item) => ({
          text: `${item.location_id} ${item.name}`,
          value: item.location_id,
        }))
      },
      statuses() {
        return getRefundStatuses()
      },
      /**
       * Whether the current role may see/filter customer information, driven by the
       * country display settings through the `addToHeaders` mixin. Gates both the
       * Customer display column and the customer filter fields.
       */
      customerFilterAllowed() {
        return !!(
          this.roles &&
          this.roles.length &&
          this.addToHeaders(this.displayFieldSettings, 'customer_name', this.roles[0])
        )
      },
      /**
       * Fields offered in the FilterDialog: the table columns plus the dedicated customer
       * filters. The single "customer" column is display-only; customer search is split
       * into three text filters (first name / last name / customer id) keyed with Mongo
       * dot notation so the API matches the nested `customer` sub-document.
       */
      filterFields() {
        const fields = [...this.headers]
        if (this.customerFilterAllowed) {
          fields.push(
            {
              text: this.$t('subOrder.firstName'),
              value: 'customer.first_name',
              filterType: 'text',
            },
            {
              text: this.$t('subOrder.lastName'),
              value: 'customer.last_name',
              filterType: 'text',
            },
            {
              text: this.$t('refund.customerId'),
              value: 'customer.customer_id',
              filterType: 'text',
            }
          )
        }
        return fields
      },
      headers() {
        const headers = [
          {
            text: this.$t('refund.reference'),
            value: 'refund_id',
            filterType: 'text',
          },
          {
            text: this.$t('refund.numOrder'),
            value: 'order_id',
            filterType: 'text',
          },
          {
            text: this.$t('refund.numReturn'),
            value: 'return_id',
            filterType: 'text',
          },
          {
            text: this.$t('refund.status'),
            value: 'current_status',
            filterType: 'array',
            arrayValues: this.statuses,
          },
          {
            text: this.$t('refund.amountWithTax'),
            value: 'amount',
            filterType: 'amountRange',
          },
          {
            text: this.$t('refund.amountWithoutTax'),
            value: 'amount_exclude_tax',
            filterType: 'amountRange',
          },
        ]
        // Customer column visibility is driven by the country display settings:
        // hidden for roles not allowed to see customer information. It is a display-only
        // column (full name via getCustomerName); customer filtering is offered as three
        // dedicated fields in `filterFields` (first name / last name / customer id).
        if (this.customerFilterAllowed) {
          headers.push({
            text: this.$t('refund.customer'),
            value: 'customer',
            sortable: false,
          })
        }
        headers.push(
          {
            text: this.$t('refund.sourceLocation'),
            value: 'source_location_id',
            // Source-location filter: location picker (like origin_location_id), restricted to
            // Admin / Service Client (RTLOMS-542). Other profiles keep the column but cannot
            // filter by location — `filterType: undefined` makes FilterDialog skip the field.
            filterType: this.isCustomerService ? 'array' : undefined,
            arrayValues: this.filterLocations,
          },
          {
            text: this.$t('refund.createdAt'),
            value: 'created_at',
            filterType: 'date',
          },
          {
            text: this.$t('refund.refundedAt'),
            value: 'refund_date',
            filterType: 'date',
          },
          {
            text: '',
            value: 'action',
            sortable: false,
          }
        )
        // Store view: the store-code column is implicit (single store), so it is removed.
        return this.restrictToCurrentStore ? headers.filter((column) => column.value !== 'source_location_id') : headers
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 25, 50],
          'items-per-page-text': this.$t('refund.refundsPerPage'),
        }
      },
    },
  }
</script>
